import { useEffect, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getPracticeMetricsTrend } from 'api/marketing';
import {
    IPracticeMetricsQuery,
    IPracticeAdTrend,
    IAdMetricsTrendDisplay,
    IAdMetricsTrend,
    PausePeriod,
} from 'types/marketing';

dayjs.extend(utc);

function transformTrendToDisplay(
    trends: IAdMetricsTrend[],
    pauseTimeRanges: PausePeriod[],
    week: number,
    joinedAt?: string,
): IAdMetricsTrendDisplay {
    const today = dayjs().startOf('day');
    const joinedAtDate = joinedAt ? dayjs.utc(joinedAt).local().startOf('day') : today.subtract(7, 'day');

    let startDate: dayjs.Dayjs;
    const endDate = today;

    if (week === 0) {
        startDate = joinedAtDate;
    } else {
        const weeksAgo = today.subtract(week * 7, 'day');
        startDate = weeksAgo.isAfter(joinedAtDate) ? weeksAgo : joinedAtDate;
    }

    const dates: string[] = [];
    const bookings: number[] = [];
    const impressionShareRate: number[] = [];

    let currentDate = startDate;
    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate)) {
        const dateKey = currentDate.format('YYYY-MM-DD');
        const trend = trends.find((t) => dayjs(t.date).format('YYYY-MM-DD') === dateKey);

        dates.push(currentDate.format('MM/DD/YYYY'));
        bookings.push(trend?.bookings ?? 0);

        if (trend && trend.auctionParticipants && trend.impressions && trend.auctionParticipants > 0) {
            impressionShareRate.push(Math.round((trend.impressions / trend.auctionParticipants) * 100));
        } else {
            impressionShareRate.push(0);
        }

        currentDate = currentDate.add(1, 'day');
    }

    const uniquePauseTimeRanges = pauseTimeRanges.filter((range, index, array) => {
        return array.findIndex((r) => r.start === range.start && r.end === range.end) === index;
    });
    const pauseTimeRangesDisplay = uniquePauseTimeRanges.map((r) => ({
        start: dayjs.utc(r.start).local().format('MM/DD/YYYY'),
        end: dayjs.utc(r.end).local().format('MM/DD/YYYY'),
    }));

    return {
        dates,
        bookings,
        impressionShareRate,
        pauseTimeRanges: pauseTimeRangesDisplay,
    };
}

const usePracticeMetricsTrend = (
    initialQuery: IPracticeMetricsQuery,
    joinedAt?: string,
): [IAdMetricsTrendDisplay | undefined, boolean, (params: IPracticeMetricsQuery) => void] => {
    const [loading, setLoading] = useState(false);
    const [trendDisplay, setTrendDisplay] = useState<IAdMetricsTrendDisplay>();
    const [currentJoinedAt, setCurrentJoinedAt] = useState(joinedAt);

    useEffect(() => {
        if (joinedAt) setCurrentJoinedAt(joinedAt);
    }, [joinedAt]);

    const fetchData = useCallback(
        async (params: IPracticeMetricsQuery) => {
            try {
                setLoading(true);
                const result = await getPracticeMetricsTrend(params);
                if (!result.error && result?.data?.data) {
                    const data: IPracticeAdTrend = result.data.data;
                    setTrendDisplay(
                        transformTrendToDisplay(
                            data.trends || [],
                            data.pauseTimeRanges || [],
                            params.week,
                            currentJoinedAt,
                        ),
                    );
                }
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        },
        [currentJoinedAt],
    );

    useEffect(() => {
        if (currentJoinedAt) {
            fetchData(initialQuery);
        }
    }, [currentJoinedAt]);

    return [trendDisplay, loading, fetchData];
};

export default usePracticeMetricsTrend;
